import {
  AMENDMENT_REASONS,
  AMENDMENT_SECURITY_PCT,
  AMENDMENT_STATUSES,
  type AmendmentReason,
  type AmendmentStatus,
  type AmendmentView,
  APPROVAL_STEPS,
  type ApprovalStep,
  approvalStepPerms,
  CHANGE_CLASSES,
  CHANGE_STATUSES,
  CHANGE_TYPES,
  type ChangeDetail,
  type ChangeLine,
  type ChangeSummary,
  type ContractDetail,
  can,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useCostArticles } from '../../lib/articles';
import { date, dateTime, tenge, todayLocal } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, type Tone, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { NewChangeDialog } from '../changes/ChangesPage';
import {
  fromItemsDraft,
  fromScheduleDraft,
  ItemsRowsEditor,
  itemsDraftTotal,
  itemsDraftValid,
  type ScheduleDraftRow,
  ScheduleRowsEditor,
  toItemsDraft,
  toScheduleDraft,
} from './editors';

type Target = {
  legalEntityId?: string;
  directionId?: string | null;
  projectId?: string;
  projectPath?: Array<string | null>;
};

const STATUS_TONE: Record<AmendmentStatus, Tone> = {
  draft: 'neutral',
  approval: 'info',
  approved: 'success',
  signed: 'ink',
  cancelled: 'neutral',
};

const ACTIVE: AmendmentStatus[] = ['draft', 'approval', 'approved'];

/** Сравнение «было → стало»: на узком экране строки складываются в карточки */
export function ChangesList({ changes }: { changes: ChangeLine[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-line">
      <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3 bg-surface-2 px-3 py-1.5 text-[12px] text-ink-2 md:grid">
        <span>Условие</span>
        <span>Было</span>
        <span>Стало</span>
      </div>
      <ul className="divide-y divide-line">
        {changes.map((c, i) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: строки сравнения без своих id, порядок постоянный
            key={i}
            className="grid gap-1 px-3 py-2 text-[13px] md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] md:gap-3"
          >
            <span className="font-medium text-ink">{c.label}</span>
            <span className="text-ink-2 [overflow-wrap:anywhere]">
              <span className="md:hidden">было: </span>
              {c.before}
            </span>
            <span className="text-ink [overflow-wrap:anywhere]">
              <span className="text-ink-2 md:hidden">стало: </span>
              {c.after}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** ДГ-04: согласующий видит, что изменилось в проекте договора с прошлой отправки */
export function VersionChanges({ d }: { d: ContractDetail }) {
  if (!d.versionChanges.length) return null;
  const draft = d.status === 'draft';
  return (
    <div className="mb-4 flex flex-col gap-2">
      <div>
        <p className="text-[14px] font-medium text-ink">
          {draft ? 'Правки с последней отправки на согласование' : 'Что изменилось с прошлой отправки'}
        </p>
        <p className="text-[12px] text-ink-2">
          {draft
            ? 'Согласующие увидят это сравнение и подтвердят изменения.'
            : 'Согласуя свой пункт, вы подтверждаете эти изменения.'}
        </p>
      </div>
      <ChangesList changes={d.versionChanges} />
    </div>
  );
}

/**
 * Условия допсоглашения из утверждённого запроса на изменение (ДГ-08). Сумма — изменение по этому договору в
 * анализе влияния запроса; у доходного договора, если он в запросе один, — изменение выручки (себестоимость
 * запроса к сумме расходного договора не относится). Срок — сдвиг в днях. Суммы запроса видны с уровнем
 * «Все деньги»: без него сумма не подставляется. Обоснование — номер и вид запроса, без его текста: в тексте
 * запроса суммы, закрытые уровнем «Все деньги», а у допсоглашения — уровень «Акты и счета».
 */
function fromChangeTerms(d: ContractDetail, c: ChangeDetail) {
  const own = c.items.find((i) => i.linkType === 'contract' && i.linkId === d.id && i.amountDelta);
  const contractsInChange = c.items.filter((i) => i.linkType === 'contract').length;
  const delta =
    own?.amountDelta ??
    (d.direction === 'revenue' && contractsInChange === 1 && c.revenueDelta ? c.revenueDelta : null);
  const amount = delta != null && d.amount != null ? Math.round((d.amount + delta) * 100) / 100 : null;
  let end = '';
  if (c.daysDelta && d.endDate) {
    const x = new Date(`${d.endDate}T00:00:00Z`);
    x.setUTCDate(x.getUTCDate() + c.daysDelta);
    end = x.toISOString().slice(0, 10);
  }
  const kind: AmendmentReason =
    c.type === 'psd'
      ? 'psd_revision'
      : c.changeClass === 'customer'
        ? 'extra_works'
        : c.type === 'contract'
          ? 'terms'
          : 'other';
  return {
    reasonKind: kind,
    reason: `По запросу на изменение ${c.number} (${CHANGE_TYPES[c.type].toLowerCase()}, ${CHANGE_CLASSES[c.changeClass].toLowerCase()}): `,
    newAmount: amount != null && amount > 0 ? String(amount) : '',
    newEndDate: end,
  };
}

function AmendmentDialog({
  d,
  amendment,
  fromChange,
  onClose,
  onSaved,
}: {
  d: ContractDetail;
  amendment?: AmendmentView;
  /** Новое допсоглашение по утверждённому запросу на изменение */
  fromChange?: ChangeDetail;
  onClose: () => void;
  onSaved: (x: ContractDetail) => void;
}) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const articles = useCostArticles();
  const a = amendment;
  const pre = !a && fromChange ? fromChangeTerms(d, fromChange) : null;
  const initial = {
    reasonKind: (a?.reasonKind ?? pre?.reasonKind ?? 'psd_revision') as AmendmentReason,
    reason: a?.reason ?? pre?.reason ?? '',
    newAmount: a?.newAmount != null && !a.items ? String(a.newAmount) : (pre?.newAmount ?? ''),
    newEndDate: a?.newEndDate ?? pre?.newEndDate ?? '',
    newPaymentTerms: a?.newPaymentTerms ?? '',
    newCostArticle: a?.newCostArticle ?? '',
    scopeNote: a?.scopeNote ?? '',
    securityCheck: a?.securityCheck ?? false,
  };
  const start = useRef(initial).current;
  const [f, setF] = useState(initial);
  // Без доступа к суммам текст с суммами виден с маской: менять его может только тот, кто их видит
  const masked = (v: string) => !money && v.includes('сумма скрыта');
  const [editItems, setEditItems] = useState(Boolean(a?.items));
  const [items, setItems] = useState(toItemsDraft(a?.items ?? d.items));
  const [editSchedule, setEditSchedule] = useState(Boolean(a?.schedule));
  const [schedule, setSchedule] = useState<ScheduleDraftRow[]>(toScheduleDraft(a?.schedule ?? d.schedule));
  const amount = Number(f.newAmount.replace(/\s/g, '').replace(',', '.'));
  const amountOk = !f.newAmount.trim() || (Number.isFinite(amount) && amount > 0);
  // Без права видеть суммы сумма скрыта: процент изменения не считается, проверку решает сервер
  const current = d.amount ?? 0;
  const nextAmount = editItems ? itemsDraftTotal(items) : f.newAmount.trim() && amountOk ? amount : current;
  const pct = current ? (Math.abs(nextAmount - current) / current) * 100 : 0;
  const counterparty = f.reasonKind === 'counterparty_change';
  const save = useMutation({
    mutationFn: () => {
      const full = {
        reasonKind: f.reasonKind,
        reason: f.reason.trim(),
        newEndDate: f.newEndDate || null,
        newPaymentTerms: f.newPaymentTerms.trim() || null,
        newCostArticle: f.newCostArticle || null,
        scopeNote: f.scopeNote.trim() || null,
        securityCheck: counterparty || f.securityCheck,
        ...(!a && fromChange ? { changeId: fromChange.id } : {}),
      };
      // При правке уходят только изменённые поля: нетронутый текст с маской сумм не затирает исходный
      const changed = a
        ? Object.fromEntries(
            Object.entries(full).filter(([k, v]) => {
              const was = start[k as keyof typeof start];
              const norm = (x: unknown) => (typeof x === 'string' ? x.trim() : x) || null;
              return k === 'securityCheck'
                ? v !== (counterparty || start.securityCheck)
                : norm(v) !== norm(was);
            }),
          )
        : full;
      const body = {
        ...changed,
        // Сумма, спецификация и графики — только у тех, кто видит суммы договоров
        ...(money
          ? {
              ...(editItems ? {} : { newAmount: f.newAmount.trim() ? amount : null }),
              items: editItems ? fromItemsDraft(items) : null,
              schedule: editSchedule ? fromScheduleDraft(schedule) : null,
            }
          : {}),
      };
      return a
        ? api<ContractDetail>(`/contracts/${d.id}/amendments/${a.id}`, { method: 'PATCH', body })
        : api<ContractDetail>(`/contracts/${d.id}/amendments`, { body });
    },
    onSuccess: (x) => {
      onSaved(x);
      toast(a ? `Допсоглашение № ${a.number} сохранено` : 'Проект допсоглашения создан');
      onClose();
    },
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const ready =
    f.reason.trim().length >= 5 && amountOk && (!editItems || itemsDraftValid(items)) && !save.isPending;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title={
        a
          ? `Допсоглашение № ${a.number} к договору ${d.number}`
          : fromChange
            ? `Допсоглашение к договору ${d.number} по ${fromChange.number}`
            : `Допсоглашение к договору ${d.number}`
      }
      description="Меняет сумму, сроки, объёмы и порядок оплаты подписанного договора. После подписания карточка договора обновится, прежние условия останутся в истории."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {a ? 'Сохранить допсоглашение' : 'Создать проект допсоглашения'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Причина">
            <Select
              value={f.reasonKind}
              onChange={(e) => set('reasonKind', e.target.value as AmendmentReason)}
            >
              {(Object.keys(AMENDMENT_REASONS) as AmendmentReason[]).map((k) => (
                <option key={k} value={k}>
                  {AMENDMENT_REASONS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Новый срок окончания" hint={`Сейчас: ${d.endDate ? date(d.endDate) : 'не указан'}`}>
            <Input type="date" value={f.newEndDate} onChange={(e) => set('newEndDate', e.target.value)} />
          </Field>
          <Field
            label="Обоснование"
            className="sm:col-span-2"
            hint="Ссылка на причину: номер ревизии ПСД, письмо заказчика, акт на допработы"
          >
            <Textarea
              rows={2}
              value={f.reason}
              readOnly={masked(start.reason)}
              onChange={(e) => set('reason', e.target.value)}
            />
          </Field>
          {money ? (
            <Field
              label="Новая сумма договора, ₸"
              hint={
                editItems
                  ? `Считается по новой спецификации: ${tenge(itemsDraftTotal(items))}`
                  : `Сейчас: ${tenge(d.amount)}. Пусто — сумма не меняется`
              }
              error={amountOk ? undefined : 'Введите сумму больше нуля'}
            >
              <Input
                inputMode="decimal"
                value={editItems ? '' : f.newAmount}
                disabled={editItems}
                placeholder={editItems ? 'по спецификации' : ''}
                onChange={(e) => set('newAmount', e.target.value)}
              />
            </Field>
          ) : null}
          {money ? (
            <Field
              label="Новый порядок оплаты"
              hint={`Сейчас: ${d.paymentTerms || 'не указан'}. Пусто — не меняется`}
            >
              <Textarea
                rows={2}
                className="min-h-10"
                value={f.newPaymentTerms}
                onChange={(e) => set('newPaymentTerms', e.target.value)}
              />
            </Field>
          ) : null}
          {money && d.direction === 'expense' ? (
            <Field
              label="Статья ФП"
              className="sm:col-span-2"
              hint={`Сейчас: ${d.costArticle ?? 'не указана'}. Увеличение договора сверяется с остатком статьи утверждённого ФП`}
            >
              <Select value={f.newCostArticle} onChange={(e) => set('newCostArticle', e.target.value)}>
                <option value="">Без изменений</option>
                {articles
                  .filter((x) => x !== d.costArticle)
                  .map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
              </Select>
            </Field>
          ) : null}
          {!money ? (
            <p className="text-[12px] text-ink-2 sm:col-span-2">
              Сумму, порядок оплаты, статью ФП, спецификацию и графики меняет сотрудник с доступом к суммам
              договоров; здесь — срок, объёмы и обоснование.
            </p>
          ) : null}
          <Field label="Изменение объёмов" className="sm:col-span-2" hint="Что добавляется или исключается">
            <Textarea
              rows={2}
              className="min-h-10"
              value={f.scopeNote}
              readOnly={masked(start.scopeNote)}
              onChange={(e) => set('scopeNote', e.target.value)}
            />
          </Field>
          {masked(start.reason) || masked(start.scopeNote) ? (
            <p className="text-[12px] text-ink-2 sm:col-span-2">
              Тексты с суммами открыты только для чтения: их меняет сотрудник с доступом к суммам договоров.
            </p>
          ) : null}
        </div>

        {money ? (
          <section className="flex flex-col gap-3 border-t border-line pt-4">
            <Checkbox
              label="Изменить спецификацию"
              description="Новые объёмы и цены; сумма договора станет суммой спецификации"
              checked={editItems}
              onChange={(e) => setEditItems(e.target.checked)}
            />
            {editItems ? <ItemsRowsEditor rows={items} onChange={setItems} /> : null}
            <Checkbox
              label="Изменить графики поставок и платежей"
              description="Платежи в процентах пересчитаются от новой суммы"
              checked={editSchedule}
              onChange={(e) => setEditSchedule(e.target.checked)}
            />
            {editSchedule ? (
              <ScheduleRowsEditor rows={schedule} onChange={setSchedule} items={d.items} />
            ) : null}
          </section>
        ) : null}

        <section className="border-t border-line pt-4">
          <Checkbox
            label="Повторная проверка службой безопасности"
            description={
              counterparty
                ? 'Смена руководителя, участников или бенефициаров контрагента: проверка обязательна'
                : pct >= AMENDMENT_SECURITY_PCT
                  ? `Сумма меняется на ${pct.toFixed(0)} %: от ${AMENDMENT_SECURITY_PCT} % проверка включается сама`
                  : `Существенное изменение условий. При изменении суммы от ${AMENDMENT_SECURITY_PCT} % проверка включается сама`
            }
            checked={counterparty || f.securityCheck || pct >= AMENDMENT_SECURITY_PCT}
            disabled={counterparty || pct >= AMENDMENT_SECURITY_PCT}
            onChange={(e) => set('securityCheck', e.target.checked)}
          />
        </section>
        <ErrorBox error={save.error} />
      </div>
    </Dialog>
  );
}

function AmendmentCard({
  d,
  a,
  target,
  onSaved,
  onEdit,
}: {
  d: ContractDetail;
  a: AmendmentView;
  target: Target;
  onSaved: (x: ContractDetail) => void;
  onEdit: () => void;
}) {
  const me = useMe();
  const [comment, setComment] = useState('');
  const [signDate, setSignDate] = useState(todayLocal());
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const revenue = d.direction === 'revenue';
  const canEdit = can(me.access, 'contracts.edit', target);
  const canSign = revenue
    ? can(me.access, 'contracts.sign', { legalEntityId: d.legalEntityId })
    : can(me.access, 'contracts.sign', target) || can(me.access, 'contracts.docs', target);
  const isOwner = Boolean(me.tenant?.isOwner);
  const money = seesMoney(me.access, 'acts_invoices');
  const decidedHere = a.approvals.some((x) => x.decidedById === me.user.id);
  // Каждый согласует свой пункт (ДГ-04); автор — только пункт инициатора; изменение сумм — тот, кто их видит
  const blindMoney = a.moneyChange && !money;
  const mayDecide = (step: ApprovalStep) =>
    a.status === 'approval' &&
    (!decidedHere || isOwner) &&
    !blindMoney &&
    (step === 'initiator' || a.createdById !== me.user.id || isOwner) &&
    approvalStepPerms(step).some((perm) => can(me.access, perm, target));
  const anyDecision = a.approvals.some((x) => x.status === 'pending' && mayDecide(x.step));
  const myStep = a.approvals.some(
    (x) => x.status === 'pending' && approvalStepPerms(x.step).some((perm) => can(me.access, perm, target)),
  );
  const act = useMutation({
    mutationFn: (p: { path: string; body?: unknown }) =>
      api<ContractDetail>(`/contracts/${d.id}/amendments/${a.id}${p.path}`, { method: 'POST', body: p.body }),
    onSuccess: (x) => {
      onSaved(x);
      setComment('');
      toast('Готово');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const signable = revenue ? a.status === 'draft' || a.status === 'approved' : a.status === 'approved';
  return (
    <article className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="text-[15px] font-semibold text-ink">Допсоглашение № {a.number}</h3>
        <Badge tone={STATUS_TONE[a.status]}>{AMENDMENT_STATUSES[a.status]}</Badge>
        <span className="text-[13px] text-ink-2">
          {a.status === 'signed' && a.signedDate
            ? `подписано ${date(a.signedDate)}`
            : `${a.createdByName ?? ''}, ${dateTime(a.createdAt)}`}
        </span>
      </div>
      <p className="text-[14px] text-ink">
        <span className="text-ink-2">{AMENDMENT_REASONS[a.reasonKind]}:</span> {a.reason}
      </p>
      {a.change ? (
        <p className="text-[14px]">
          <span className="text-ink-2">По запросу на изменение </span>
          <Link
            to="/changes/$changeId"
            params={{ changeId: a.change.id }}
            className="inline-flex min-h-11 items-center font-medium text-accent underline-offset-2 hover:underline md:min-h-0"
          >
            {a.change.number ?? 'открыть'}
          </Link>
        </p>
      ) : null}
      {a.changes.length ? (
        <ChangesList changes={a.changes} />
      ) : (
        <p className="text-[13px] text-ink-2">Условия договора не меняются.</p>
      )}
      {a.roundChanges.length ? (
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-[14px] font-medium text-ink">
              {a.status === 'draft'
                ? 'Правки с последней отправки на согласование'
                : 'Что изменилось с прошлого раунда'}
            </p>
            <p className="text-[12px] text-ink-2">
              {a.status === 'draft'
                ? 'Согласующие увидят это сравнение и подтвердят изменения.'
                : 'Согласуя свой пункт, вы подтверждаете эти изменения (ДГ-04).'}
            </p>
          </div>
          <ChangesList changes={a.roundChanges} />
        </div>
      ) : null}
      {a.status === 'cancelled' && a.cancelReason ? (
        <p className="text-[13px] text-ink">
          <span className="text-ink-2">Отменено:</span> {a.cancelReason}
        </p>
      ) : null}
      {a.scopeNote ? (
        <p className="text-[13px] text-ink">
          <span className="text-ink-2">Объёмы:</span> {a.scopeNote}
        </p>
      ) : null}

      {a.approvals.length && a.status !== 'cancelled' ? (
        <ul className="flex flex-col gap-2 rounded-md bg-surface-2 px-3 py-2.5">
          {a.approvals.map((x) => (
            <li key={x.id} className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[13px] text-ink">{APPROVAL_STEPS[x.step].title}</span>
                <Badge
                  tone={
                    x.status === 'approved'
                      ? 'success'
                      : x.status === 'skipped'
                        ? 'neutral'
                        : x.status === 'pending'
                          ? x.overdue
                            ? 'danger'
                            : 'neutral'
                          : 'warning'
                  }
                >
                  {x.status === 'approved'
                    ? 'согласовано'
                    : x.status === 'pending'
                      ? a.status !== 'approval'
                        ? 'не рассмотрено'
                        : x.overdue
                          ? 'просрочено'
                          : `до ${date(x.dueAt)}`
                      : x.status === 'rejected'
                        ? 'отклонено'
                        : x.status === 'skipped'
                          ? 'пропущен'
                          : 'на доработку'}
                </Badge>
              </div>
              {x.decidedAt ? (
                <p className="text-[12px] text-ink-2">
                  {x.approverName ?? 'портал'}, {dateTime(x.decidedAt)}
                  {x.comment ? ` — ${x.comment}` : ''}
                </p>
              ) : null}
              {x.status === 'pending' && mayDecide(x.step) ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    loading={act.isPending}
                    onClick={() => act.mutate({ path: `/approvals/${x.id}`, body: { decision: 'approved' } })}
                  >
                    Согласовать
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!comment.trim()}
                    onClick={() =>
                      act.mutate({ path: `/approvals/${x.id}`, body: { decision: 'returned', comment } })
                    }
                  >
                    На доработку
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!comment.trim()}
                    onClick={() =>
                      act.mutate({ path: `/approvals/${x.id}`, body: { decision: 'rejected', comment } })
                    }
                  >
                    Отклонить
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {anyDecision ? (
        <Textarea
          rows={2}
          className="min-h-16"
          placeholder="Комментарий для доработки или отказа"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      ) : null}
      {a.status === 'approval' && decidedHere && !isOwner ? (
        <p className="text-[13px] text-ink-2">
          Вы уже приняли решение в этом раунде: остальные пункты согласуют другие люди.
        </p>
      ) : a.status === 'approval' && blindMoney && myStep ? (
        <p className="text-[13px] text-ink-2">
          Допсоглашение меняет суммы, цены или порядок оплаты: его согласует сотрудник с доступом к суммам
          договоров.
        </p>
      ) : null}

      {signable && canSign ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Дата подписания">
            <Input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} />
          </Field>
          <Button
            variant="primary"
            loading={act.isPending}
            onClick={() => act.mutate({ path: '/sign', body: { signedDate: signDate, version: a.version } })}
          >
            Отметить подписание
          </Button>
        </div>
      ) : null}

      {canEdit && ACTIVE.includes(a.status) ? (
        <div className="flex flex-wrap gap-2">
          {a.status === 'draft' ? (
            <Button size="sm" variant="secondary" onClick={onEdit}>
              Изменить
            </Button>
          ) : null}
          {a.status === 'draft' && !revenue ? (
            <Button
              size="sm"
              variant="primary"
              loading={act.isPending}
              onClick={() => act.mutate({ path: '/submit' })}
            >
              Отправить на согласование
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
            Отменить допсоглашение
          </Button>
        </div>
      ) : null}
      {cancelling ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setCancelling(false)}
          title={`Отменить допсоглашение № ${a.number}?`}
          description={
            a.status === 'draft'
              ? 'Проект допсоглашения перестанет действовать; условия договора не изменятся.'
              : 'Допсоглашение уже у согласующих: маршрут остановится, условия договора не изменятся. Укажите причину — её увидят согласующие.'
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setCancelling(false)}>
                Не отменять
              </Button>
              <Button
                variant="danger"
                loading={act.isPending}
                disabled={a.status !== 'draft' && !cancelReason.trim()}
                onClick={() =>
                  act.mutate(
                    { path: '/cancel', body: { reason: cancelReason.trim() || undefined } },
                    { onSuccess: () => setCancelling(false) },
                  )
                }
              >
                Отменить допсоглашение
              </Button>
            </>
          }
        >
          <Field label={a.status === 'draft' ? 'Причина (необязательно)' : 'Причина отмены'}>
            <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {a.status === 'draft' && revenue ? (
        <p className="text-[12px] text-ink-2">
          Допсоглашение к доходному договору идёт без маршрута: подписание отмечает подписант компании или
          юрлица.
        </p>
      ) : null}
      <ErrorBox error={act.error} />
    </article>
  );
}

/** Допсоглашения и история версий договора (ДГ-08; Регламент, п. 8.2) */
export function AmendmentsPanel({
  d,
  target,
  onSaved,
}: {
  d: ContractDetail;
  target: Target;
  onSaved: (x: ContractDetail) => void;
}) {
  const me = useMe();
  const [editing, setEditing] = useState<AmendmentView | 'new' | ChangeDetail | null>(null);
  const [asking, setAsking] = useState(false);
  const canEdit = can(me.access, 'contracts.edit', target);
  const active = d.amendments.find((a) => ACTIVE.includes(a.status));
  const staff = me.tenant?.staff ?? false;
  // Запросы на изменение, затрагивающие договор (ДГ-08, ИЗ-07): видны с правом на изменения проекта
  const canChanges = staff && can(me.access, 'changes.view', target);
  const canAsk = staff && can(me.access, 'changes.request', target);
  const linked = useQuery({
    queryKey: ['changes', 'linked', 'contract', d.id],
    queryFn: () => api<ChangeSummary[]>(`/changes/linked/contract/${d.id}`),
    enabled: canChanges,
  });
  const withAmendment = new Set(
    d.amendments.filter((a) => a.change && a.status !== 'cancelled').map((a) => a.change!.id),
  );
  const openFromChange = useMutation({
    mutationFn: (id: string) => api<ChangeDetail>(`/changes/${id}`),
    onSuccess: (c) => setEditing(c),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Panel
      title="Допсоглашения"
      meta={d.amendments.length ? `${d.amendments.length}, история версий договора` : 'Регламент, п. 8.2'}
      actions={
        canEdit && d.status === 'signed' && !active ? (
          <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>
            Допсоглашение
          </Button>
        ) : null
      }
    >
      {d.amendments.length ? (
        <div className="flex flex-col divide-y divide-line">
          {d.amendments.map((a) => (
            <AmendmentCard
              key={a.id}
              d={d}
              a={a}
              target={target}
              onSaved={onSaved}
              onEdit={() => setEditing(a)}
            />
          ))}
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">
          Сумма, сроки, объёмы и порядок оплаты подписанного договора меняются только допсоглашением: его
          повторно согласуют затронутые подразделения, а прежние условия остаются в истории договора.
          {d.direction === 'expense'
            ? ' Оплаты по договору не могут превысить его сумму — доплата идёт после допсоглашения (ЗК-15).'
            : ''}
        </p>
      )}
      {canChanges && (linked.data?.length || canAsk) ? (
        <section className="mt-4 flex flex-col gap-2 border-t border-line pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[14px] font-semibold text-ink">Запросы на изменение по договору</h3>
            {canAsk ? (
              <Button size="sm" variant="secondary" onClick={() => setAsking(true)}>
                Запрос на изменение
              </Button>
            ) : null}
          </div>
          {linked.data?.length ? (
            <ul className="flex flex-col divide-y divide-line">
              {linked.data.map((c) => {
                const ready = ['approved', 'implemented'].includes(c.status);
                const done = withAmendment.has(c.id);
                return (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[14px]">
                    <Link
                      to="/changes/$changeId"
                      params={{ changeId: c.id }}
                      className="inline-flex min-h-11 items-center font-medium text-accent underline-offset-2 hover:underline md:min-h-0"
                    >
                      {c.number}
                    </Link>
                    <span className="min-w-0 flex-1 text-ink">{c.title}</span>
                    <Badge>{CHANGE_STATUSES[c.status]}</Badge>
                    {c.atRisk ? <Badge tone="danger">работы до допсоглашения</Badge> : null}
                    {done ? <span className="text-[13px] text-ink-2">допсоглашение оформлено</span> : null}
                    {ready && !done && canEdit && d.status === 'signed' && !active ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={openFromChange.isPending && openFromChange.variables === c.id}
                        onClick={() => openFromChange.mutate(c.id)}
                      >
                        Допсоглашение по запросу
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-[13px] text-ink-2">
              Изменения цены, объёмов и сроков начинаются с запроса на изменение: после его утверждения отсюда
              оформляется допсоглашение (ДГ-08).
            </p>
          )}
        </section>
      ) : null}
      {editing ? (
        <AmendmentDialog
          d={d}
          amendment={editing === 'new' || 'changeClass' in editing ? undefined : editing}
          fromChange={editing !== 'new' && 'changeClass' in editing ? editing : undefined}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      ) : null}
      {asking ? (
        <NewChangeDialog
          projectId={d.projectId}
          preset={{
            type: 'contract',
            links: [{ linkType: 'contract', linkId: d.id, title: `Договор ${d.number}` }],
          }}
          onClose={() => setAsking(false)}
        />
      ) : null}
    </Panel>
  );
}
